import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { CreditCard, DollarSign, TrendingUp, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentDiversity = () => {
  const { selectedProperty } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (selectedProperty) {
      fetchData();
    }
  }, [selectedProperty]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('access_token');
      
      const [methodsRes, transactionsRes, statsRes] = await Promise.all([
        axios.get(`${API}/payment-diversity/methods`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payment-diversity/transactions?limit=20`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/payment-diversity/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setPaymentMethods(methodsRes.data);
      setTransactions(transactionsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load payment data');
    } finally {
      setLoading(false);
    }
  };

  const addPaymentMethod = async (type) => {
    try {
      const token = localStorage.getItem('access_token');
      
      await axios.post(`${API}/payment-diversity/methods/add?method_type=${type}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Payment method added!');
      fetchData();
    } catch (error) {
      toast.error('Failed to add payment method');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'failed':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  if (!selectedProperty) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-slate-600">Please select a property</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-full">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="payment-diversity-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Payment Diversity</h1>
          <p className="text-slate-600 mt-1">Manage multiple payment methods and options</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-blue-600">{stats.total_transactions}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Success Rate</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-emerald-600">{stats.success_rate}%</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Total Revenue</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-purple-600">${stats.total_revenue.toFixed(2)}</div>
            </CardContent>
          </Card>

          <Card className="border-l-4 border-l-red-500">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-600">Failed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-red-600">{stats.failed_transactions}</div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Payment Methods</CardTitle>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => addPaymentMethod('credit_card')}>
                  + Card
                </Button>
                <Button size="sm" variant="outline" onClick={() => addPaymentMethod('paypal')}>
                  + PayPal
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {paymentMethods.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <CreditCard className="mx-auto mb-2" size={40} />
                <p>No payment methods added</p>
              </div>
            ) : (
              <div className="space-y-3">
                {paymentMethods.map((method) => (
                  <div key={method.id} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white">
                      <CreditCard size={20} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium capitalize">{method.brand || method.type}</span>
                        {method.is_default && <Badge>Default</Badge>}
                      </div>
                      <p className="text-sm text-slate-600">
                        {method.last_four && `•••• ${method.last_four}`}
                        {method.exp_month && ` • Exp ${method.exp_month}/${method.exp_year}`}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={20} />
              Payment Features
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                    <DollarSign size={20} />
                  </div>
                  <h3 className="font-semibold">Split Payments</h3>
                </div>
                <p className="text-sm text-slate-600">
                  Split payments across multiple cards or methods
                </p>
              </div>

              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white">
                    <Calendar size={20} />
                  </div>
                  <h3 className="font-semibold">Installment Plans</h3>
                </div>
                <p className="text-sm text-slate-600">
                  Offer flexible payment plans (3, 6, or 12 months)
                </p>
              </div>

              <div className="p-4 bg-emerald-50 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white">
                    <CreditCard size={20} />
                  </div>
                  <h3 className="font-semibold">Multiple Processors</h3>
                </div>
                <p className="text-sm text-slate-600">
                  Support for Stripe, PayPal, and cryptocurrency
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {transactions.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No transactions yet
            </div>
          ) : (
            <div className="space-y-3">
              {transactions.map((txn) => (
                <div key={txn.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-cyan-400 to-blue-600 flex items-center justify-center text-white">
                      <DollarSign size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">${txn.amount.toFixed(2)}</span>
                        <Badge className={getStatusColor(txn.status)}>
                          {txn.status}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600 capitalize">
                        {txn.payment_method_type} • {txn.provider}
                      </p>
                      <p className="text-xs text-slate-500">
                        {format(new Date(txn.created_at), 'PPp')}
                      </p>
                    </div>
                  </div>
                  {txn.transaction_id && (
                    <span className="text-xs text-slate-500">{txn.transaction_id}</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentDiversity;
